import { sideOf, type PasurView } from "@bing/engine-pasur";
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { playSfx, type SfxName } from "../sound/sfx";
import { collectSweep, type SeatEdge } from "./card-motion";
import {
  collapsible,
  frameMoved,
  momentTiming,
  momentView,
  momentsBetween,
  openingDealAt,
  type Moment,
} from "./pasur-moments";

/*
 * پاسور'S CAPTURE IS ALWAYS SEEN — the table's own hold, in `use-trick-hold.ts`'s spirit (P1 Task 5;
 * the reference's D11: a non-trick game builds its own hold for its own «move resolves» moment).
 *
 * Operator, 2026-10-10: پاسور must match حکم and بی‌دل — «the last card and the full collect animation
 * always shown». A frame used to be drawn as it came: a capture's cards just vanished, and a fast next
 * frame — a bot's play, a human's instant one, the deal's last card, the next deal — replaced the pool
 * at once. Now each MOMENT a frame brings (`pasur-moments.ts`: a capture — a سور and its cancel among
 * them — and, at a deal's end, the scoop) is LATCHED on the render where it first appears and stays
 * on screen for its whole hold + collect from then, whatever newer frames say:
 *
 *   the hold     the played card flies onto the set it takes (a trailed last card onto the pool);
 *   the reveal   at the hold's end the set — the remainder — starts sliding to its taker's seat
 *                (`collectSweep`), its cue sounds, a سور's or the scoop's banner appears, and the
 *                سور count moves (nothing before the card has landed: spoiler discipline);
 *   the release  at the collect's end the next moment shows, or the live frame — cards that landed
 *                meanwhile fly in from their seats at that moment.
 *
 * A moment that a frame brings while another is on screen WAITS its turn (never cuts it short); a
 * backlog past CAPTURE_BACKLOG waiting PLAIN captures drops the oldest of them — never the one on
 * screen, a سور, its cancel or a deal's end (`collapsible`, fix round 1: a burst used to drop those
 * too) — so a tab back from the background does not replay a minute of captures with the hand
 * disabled, and nothing that carries a banner, a cue or a deal's points is ever skipped. A mount adopts
 * the frame as it stands — a resume or a rejoin replays nothing — as does a frame that is no
 * continuation (another seat, a new match); the one frame a mount does not adopt is the match's
 * FIRST, which is its opening deal (Task 6: `openingDealAt` — a solo game never saves that frame).
 *
 * THE DEAL (Task 6, W14) is a moment too: every round of a deal — the opening, each refill — comes out
 * of the stock, seen whole (`use-pasur-deal.ts` flies its cards), the hand disabled until its last
 * card has landed; a frame that lands meanwhile waits its turn, like any other. So is THE DEAL'S CARD
 * (Task 7, W15): its breakdown, after the scoop and before the next deal — a hold with no slide.
 *
 * While a moment is on screen the table draws `shown` (the table as it stood before the moment's play
 * — `momentView`), the hand is disabled and «تأیید قبل از بازی کارت» is inert (`holding`).
 */

/** A revealed سور, or a سور that cancelled one (D10) — the banner's subject. `id` is per moment, so
 *  each event shows once (`useTransientNotice` keys on it), and a rematch's equal keys never alias. */
export interface SurNotice {
  readonly id: number;
  readonly kind: "scored" | "cancelled";
  /** Who cleared the table. */
  readonly by: number;
  /** A cancel: whose سور it neutralised (null: the log does not hold it — the side is named). */
  readonly victim: number | null;
}

export interface CaptureHold {
  /** A moment is on screen: nothing may be played (the hand is disabled). */
  readonly holding: boolean;
  /** The moment on screen … */
  readonly moment: Moment | null;
  /** … and its id in this hold (unique for the table's life; 0 when none). */
  readonly momentId: number;
  /** …and its slide has begun (its cue sounded, its numbers moved). */
  readonly revealed: boolean;
  /** What the table draws: the live frame, or the table as the moment's play found it. */
  readonly shown: PasurView;
  /** The newest revealed سور / cancel — its banner outlives its moment. (The scoop's label is the
   *  scoop moment's own while the remainder slides, then the deal's card's first row: Task 7.) */
  readonly sur: SurNotice | null;
  /** Attach to the pool's layer: the collect animates its `[data-sweep="1"]` elements. */
  readonly layerRef: RefObject<HTMLDivElement>;
}

/** How many PLAIN captures may wait behind the one on screen before the oldest of them drop. */
export const CAPTURE_BACKLOG = 2;

/** The queue with its backlog bounded: past CAPTURE_BACKLOG waiting plain captures, the oldest of
 *  THEM drop; the moment on screen and every moment that is not `collapsible` always stay. */
function bounded(queue: readonly Queued[]): readonly Queued[] {
  const plain = queue.slice(1).filter((q) => collapsible(q.m));
  if (plain.length <= CAPTURE_BACKLOG) return queue;
  const gone = new Set(plain.slice(0, plain.length - CAPTURE_BACKLOG).map((q) => q.id));
  return queue.filter((q) => !gone.has(q.id));
}

/** The collect runs this far past the release, invisible: the RELEASE ends it — never its own finish
 *  — so whatever waits for the page's animations (the layout probe's settle) waits for the release. */
const COLLECT_TAIL_MS = 120;

/** A drawn seat's edge → `collectSweep`'s relative seat (its four sweeps, physical). */
const EDGE_REL: Record<SeatEdge, number> = { bottom: 0, right: 1, top: 2, left: 3 };

interface Queued {
  readonly id: number;
  readonly m: Moment;
}

interface HoldState {
  /** The newest frame accounted for (by content: an equal frame as a new object changes nothing). */
  readonly seen: PasurView;
  /** The moment on screen first, then the ones waiting. */
  readonly queue: readonly Queued[];
  readonly next: number;
  /** The id of the moment on screen once its slide began; 0 before. */
  readonly revealed: number;
  readonly sur: SurNotice | null;
}

/** The cue a moment sounds as its slide begins: your side's capture clinks toward you, the other
 *  side's is the trick-loss shove (never for your partner's — Task 1's note); a سور and a cancel
 *  sound their own sting INSTEAD; a scoop its slide (a remainder nobody takes, nothing). */
export function cueOf(m: Moment, yourSide: number): SfxName | null {
  if (m.kind === "deal") return null; // its shuffle is the deal's own (`useDealSfx`)
  if (m.kind === "breakdown") return null; // the deal's card is silent: the scoop's cue just sounded
  if (m.kind === "scoop") return m.seat === null ? null : "scoop";
  if (m.sur?.kind === "scored") return "sur";
  if (m.sur?.kind === "cancelled") return "surCancel";
  return sideOf(m.play.seat) === yourSide ? "capture" : "trickLose";
}

function revealHead(s: HoldState): HoldState {
  const head = s.queue[0];
  if (!head || s.revealed === head.id) return s;
  const m = head.m;
  return {
    ...s,
    revealed: head.id,
    sur:
      m.kind === "capture" && m.sur
        ? { id: head.id, kind: m.sur.kind, by: m.play.seat, victim: m.sur.victim }
        : s.sur,
  };
}

/** The collect: from where the card lies to `target` (screen axes), shrinking and fading. The
 *  element sits in a slot tilted `tilt`°, so the sweep is turned back to the screen first. The last
 *  `COLLECT_TAIL_MS` hold the end state, invisible. */
function collectKeyframes(target: string, tilt: number, collectMs: number): Keyframe[] {
  const at = (sweep: string, scale: number): string =>
    `rotate(${-tilt}deg) ${sweep} rotate(${tilt}deg) scale(${scale})`;
  const end = at(target, 0.62);
  return [
    { transform: at("translate(0px, 0px)", 1), opacity: 1 },
    { transform: end, opacity: 0, offset: collectMs / (collectMs + COLLECT_TAIL_MS) },
    { transform: end, opacity: 0 },
  ];
}

/**
 * `useCaptureHold(view, edgeOf)` — call it in the TABLE (its hand, HUD and sounds read `shown`), with
 * the edge each seat is drawn on (`pasurSeatEdge`), and attach `layerRef` to the pool's layer.
 */
export function useCaptureHold(view: PasurView, edgeOf: (seat: number) => SeatEdge): CaptureHold {
  const [hold, setHold] = useState<HoldState>(() => {
    // a mount adopts the frame — unless it is the match's first: its opening deal is shown
    const opening = openingDealAt(view);
    return {
      seen: view,
      queue: opening === null ? [] : [{ id: 1, m: opening }],
      next: opening === null ? 1 : 2,
      revealed: 0,
      sur: null,
    };
  });
  // LATCH DURING RENDER, never in an effect (trick-hold's rule): the render where a moment first
  // appears must already draw it — an effect would hand it over a render late, after the next
  // frame's pool had painted.
  let cur = hold;
  if (frameMoved(hold.seen, view)) {
    const found = momentsBetween(hold.seen, view);
    if (found === null) cur = { ...hold, seen: view, queue: [], revealed: 0 };
    else {
      const queued = [...hold.queue, ...found.map((m, i) => ({ id: hold.next + i, m }))];
      cur = { ...hold, seen: view, next: hold.next + found.length, queue: bounded(queued) };
    }
    setHold(cur);
  }
  const head = cur.queue[0] ?? null;
  const headId = head?.id ?? 0;
  const { hold: holdMs, collect: collectMs } = head
    ? momentTiming(head.m)
    : { hold: 0, collect: 0 };

  // THE REVEAL at the hold's end, THE RELEASE at the collect's — timed from the moment's first commit,
  // keyed on its id (a number), so new frame objects neither restart nor end it.
  useEffect(() => {
    if (headId === 0) return undefined;
    const reveal = setTimeout(
      () => setHold((s) => (s.queue[0]?.id === headId ? revealHead(s) : s)),
      holdMs,
    );
    const release = setTimeout(
      () => setHold((s) => (s.queue[0]?.id === headId ? { ...s, queue: s.queue.slice(1) } : s)),
      holdMs + collectMs,
    );
    return () => {
      clearTimeout(reveal);
      clearTimeout(release);
    };
  }, [headId, holdMs, collectMs]);

  // THE CUE, once per moment, as its slide begins (StrictMode's second effect run is a no-op).
  const revealed = head !== null && cur.revealed === head.id;
  const cue = revealed && head ? cueOf(head.m, view.yourSide) : null;
  const sounded = useRef(0);
  useEffect(() => {
    if (cue === null || sounded.current === headId) return;
    sounded.current = headId;
    playSfx(cue);
  }, [cue, headId]);

  // THE COLLECT — one WAAPI animation per sweeping element (the set and the card that took it; the
  // remainder), holding for the hold (its delay), then sliding to the taker's seat; started once per
  // moment and dropped ONLY at the release, in a LAYOUT effect, so the live pool never inherits its
  // fade on its first paint. A runtime without `.animate` skips the flourish; the release still runs.
  const layerRef = useRef<HTMLDivElement>(null);
  const sweep = useRef<{ id: number; anims: Animation[] }>({ id: 0, anims: [] });
  const taker =
    head === null || head.m.kind === "deal" || head.m.kind === "breakdown"
      ? null
      : head.m.kind === "capture"
        ? head.m.play.seat
        : head.m.seat;
  const target = taker === null ? "translate(0px, 0px)" : collectSweep(EDGE_REL[edgeOf(taker)]);
  useLayoutEffect(() => {
    const s = sweep.current;
    if (headId === 0) {
      for (const a of s.anims) a.cancel();
      s.anims = [];
      s.id = 0;
      return;
    }
    if (s.id === headId) return; // once per moment: re-renders, timer ticks and StrictMode are no-ops
    for (const a of s.anims) a.cancel(); // the moment before it, released in this same commit
    s.anims = [];
    s.id = headId;
    const layer = layerRef.current;
    if (!layer) return;
    for (const el of layer.querySelectorAll<HTMLElement>('[data-sweep="1"]')) {
      if (typeof el.animate !== "function") continue;
      const tilt = Number(el.dataset["tilt"] ?? "0") || 0;
      s.anims.push(
        el.animate(collectKeyframes(target, tilt, collectMs), {
          duration: collectMs + COLLECT_TAIL_MS,
          delay: holdMs,
          easing: "cubic-bezier(.4,0,.2,1)",
          fill: "forwards",
        }),
      );
    }
  }, [headId, target, holdMs, collectMs]);

  return {
    holding: head !== null,
    moment: head?.m ?? null,
    momentId: headId,
    revealed,
    shown: head === null ? view : momentView(head.m, revealed),
    sur: cur.sur,
    layerRef,
  };
}
